/** All configured tokens, including jobs that have not scanned a block yet. */
import { useState } from "react";
import { Link } from "react-router";
import { useJobs, useSetPaused } from "@/api/hooks";
import type { TokenJob } from "@/api/types";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import { ScrollX } from "@/components/ScrollX";
import { SyncStatus } from "@/components/SyncStatus";
import { fmtAgoShort, fmtCompact, fmtPrice, fmtUsd } from "@/lib/format";
import { routes, syncState, tokenLabel } from "@/lib/jobs";

/** hidden on phone, where the price, holders and volume need the width */
const NARROW_HIDDEN = "narrow:hidden";
/** cell padding; tighter on phone so the five numeric columns fit 390 px */
const CELL = "p-3 narrow:px-1.5";
const COLUMNS: [string, string?][] = [
  ["Token"],
  ["Tracking", NARROW_HIDDEN],
  ["Price"],
  ["Price time", "narrow:hidden"],
  ["Priced", "hidden narrow:table-cell"],
  ["Holders"],
  ["24h volume"],
  ["Scanned block", NARROW_HIDDEN],
  ["Last scan", NARROW_HIDDEN],
];

/**
 * Pause or resume token-flow's following of a token. A job switched off in
 * config.toml is not running, so it gets no button: resuming could not start it.
 */
function PauseToggle({ job }: { job: TokenJob }) {
  const setPaused = useSetPaused(job.chain, job.token);
  const tracking = job.tracking;
  if (!tracking?.enabled) return null;
  const paused = tracking.paused;
  const onClick = () => {
    if (
      !paused &&
      !window.confirm(
        `Pause tracking ${job.symbol}? token-flow stops reading the chain for it and sends no alerts until you resume.`,
      )
    )
      return;
    setPaused.mutate(!paused);
  };
  return (
    <span className="inline-flex items-center gap-2">
      <button
        type="button"
        className="ctl"
        onClick={onClick}
        disabled={setPaused.isPending}
        title={paused ? "Resume: catch up from the last scanned block" : "Pause: stop following this token"}
      >
        {paused ? "Resume" : "Pause"}
      </button>
      {setPaused.isError && (
        <span className="text-danger" role="alert">
          {setPaused.error instanceof Error ? setPaused.error.message : "Could not save"}
        </span>
      )}
    </span>
  );
}

function timestamp(value: number | null | undefined): string {
  return value == null ? "–" : new Date(value * 1000).toLocaleString();
}

export default function HomeView() {
  const jobs = useJobs();
  const [chain, setChain] = useState("");
  if (jobs.isLoading) return <EmptyState title="loading jobs…" />;
  if (jobs.isError) return <ErrorState error={jobs.error} onRetry={() => void jobs.refetch()} />;
  const data = jobs.data?.data;
  if (!data) return null;
  const chains = [...new Set(data.tokens.map((j) => j.chain))].sort();
  const tokens = data.tokens.filter((j) => !chain || j.chain === chain);
  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-lg font-semibold">All tokens</h1>
        <label className="flex items-center gap-2 text-fg-muted">
          Chain
          <select
            className="ctl"
            aria-label="filter by chain"
            value={chain}
            onChange={(e) => setChain(e.target.value)}
          >
            <option value="">all chains</option>
            {chains.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
      </div>
      {data.tokens.length === 0 ? (
        <EmptyState
          title="No token jobs found"
          hint={
            <>
              Register a token job or check <Link to={routes.health()}>health</Link>.
            </>
          }
        />
      ) : (
        <ScrollX className="rounded border border-border">
          <table className="w-full text-left text-xs">
            <thead className="bg-bg-elev text-fg-muted">
              <tr>
                {COLUMNS.map(([heading, cls]) => (
                  <th className={`${CELL} font-medium whitespace-nowrap ${cls ?? ""}`} key={heading}>
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tokens.map((job) => (
                <tr key={job.id} className="border-t border-border hover:bg-bg-hover" data-testid="token-row">
                  <td className={CELL}>
                    <Link
                      className="font-medium underline narrow:whitespace-nowrap"
                      to={routes.overview(job.chain, job.token)}
                      title={job.token}
                    >
                      <span className="narrow:hidden">{tokenLabel(job)}</span>
                      <span className="hidden narrow:inline">{job.symbol}</span>
                    </Link>
                    <div className="mt-1 hidden text-fg-faint narrow:block">{job.chain}</div>
                    {/* the phone's Tracking column, which has no room of its own */}
                    <div className="mt-1 hidden flex-wrap items-center gap-2 narrow:flex">
                      {!syncState(job.tracking).syncing && <SyncStatus state={syncState(job.tracking)} />}
                      <PauseToggle job={job} />
                    </div>
                    {job.tracking?.name && (
                      <div className="mt-1 text-fg-faint narrow:hidden">{job.tracking.name}</div>
                    )}
                  </td>
                  <td className={`${CELL} ${NARROW_HIDDEN}`}>
                    <div className="flex items-center gap-2">
                      <SyncStatus state={syncState(job.tracking)} />
                      <PauseToggle job={job} />
                    </div>
                    {job.tracking?.last_error && (
                      <div className="mt-1 max-w-64 break-words text-danger">{job.tracking.last_error}</div>
                    )}
                  </td>
                  <td className={`num whitespace-nowrap ${CELL}`}>{fmtPrice(job.mark?.price_usd)}</td>
                  <td className={`${CELL} narrow:hidden`}>{timestamp(job.mark?.at)}</td>
                  <td
                    className={`num hidden whitespace-nowrap narrow:table-cell ${CELL}`}
                    title={timestamp(job.mark?.at)}
                  >
                    {fmtAgoShort(job.mark?.at)}
                  </td>
                  <td className={`num whitespace-nowrap ${CELL}`}>{fmtCompact(job.holders_now)}</td>
                  <td className={`num whitespace-nowrap ${CELL}`}>{fmtUsd(job.flow_24h_usd)}</td>
                  <td className={`num ${CELL} ${NARROW_HIDDEN}`}>
                    {job.tracking?.last_block?.toLocaleString() ?? "–"}
                  </td>
                  <td className={`${CELL} ${NARROW_HIDDEN}`}>{timestamp(job.tracking?.last_success_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollX>
      )}
      <p className="text-xs text-fg-faint">
        Missing samples appear as –. Scan progress and price timestamps are independent.
      </p>
      {(data.liquidity.length > 0 || data.hooks.length > 0) && (
        <div className="flex flex-wrap gap-4 text-xs">
          {data.liquidity.map((job) => (
            <Link key={job.id} className="underline" to={routes.ledgers(job.chain)}>
              liquidity · {job.chain}
            </Link>
          ))}
          {data.hooks
            .filter((job) => job.available)
            .map((job) => (
              <Link key={job.id} className="underline" to={routes.hooks(job.chain)}>
                hooks · {job.chain}
              </Link>
            ))}
        </div>
      )}
    </div>
  );
}
